// NodeTextField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodeTextField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodeTextField
 * 
 * Displays a read-only text input with a modal editor for schema-driven fields.
 * 
 * @param {object} node - Node object (for context, type label, etc.).
 * @param {string} value - Current field value.
 * @param {string} fieldLabel - Label for the field.
 * @param {Function} onChange - Callback when new text is set.
 * 
 * @returns {ReactNode} NodeTextField.
 */
const NodeTextField = ({
  node,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [text, setText] = useState(value ?? '');

  // ----- EFFECTS ----- //

  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  // ----- HANDLERS ----- //

  /**
 * Handles text change.
 *
 * @param {*} event
 */

  const handleTextChange = useCallback((event) => {
    if (isReadOnly) return;
    setText(event?.target?.value ?? '');
  }, [isReadOnly]);

  /**
 * Handles text blur.
 */
  const handleTextBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;
    onChange(text);
  }, [isReadOnly, onChange, text]);

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-text-field-container">

        <TextInput
          value={text}
          placeholder={`${fieldLabel}`}
          startIconKey="deployed_code"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid
          expandable
          multiline
          maxLength={10000}
          readOnly={isReadOnly}
          onBlur={handleTextBlur}
          onChange={isReadOnly ? undefined : handleTextChange}
        />
      </div>
    </>
  );
};

// Dev Tools
NodeTextField.displayName = 'NodeTextField';

export default NodeTextField;
